'use client'
import cn from 'classnames'
import { AnimatePresence, motion } from 'motion/react'
import { type FC, useState } from 'react'
import { IconComponent } from '@/components/Icon/Icon'
import { Illustration, illustrationOf } from '@/components/Illustration'
import type { Content } from '@/content-types'
import { asText, isFilled } from '@/lib/content-fields'
import { ContentLink, ContentNextImage, RichText, type SliceComponentProps } from '@/lib/rich-text'
import { getBackgroundColor } from '@/utils/helpers'
import { linkResolver } from '@/utils/linkResolver'
// import { cubicBezier } from 'motion'

/**
 * Props for `Spotlight`.
 */
export type SpotlightProps = SliceComponentProps<Content.SpotlightSlice>

/**
 * Component for "Spotlight" Slices.
 */
const Spotlight: FC<SpotlightProps> = ({ slice }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }
  const imageLeft = slice?.primary?.image_position === 'Left'
  const illustration = illustrationOf(slice.primary)
  const hasAccordion = slice.variation === 'withAccordion' && isFilled.group(slice.primary.accordion)

  // Determine animation direction based on accordion presence and be less if mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
  const textAnimationX = isMobile
    ? 20
    : imageLeft
      ? hasAccordion
        ? -100
        : 100 // Opposite direction when image is on left
      : hasAccordion
        ? 100
        : -100 // Original direction when image is on right

  const imageAnimationX = isMobile
    ? -20
    : imageLeft
      ? hasAccordion
        ? 100
        : -100 // Opposite direction when image is on left
      : hasAccordion
        ? -100
        : 100 // Original direction when image is on right

  const isNonDefaultVariation = slice.variation !== 'default'

  return (
    <section
      id={(slice.primary as { anchor?: string }).anchor}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={cn(getBackgroundColor(slice.primary.background), 'scroll-mt-28 overflow-x-hidden')}
    >
      <div
        className={cn(
          'container flex flex-col-reverse items-center justify-between gap-x-0 md:gap-x-0 lg:gap-x-20',
          imageLeft ? 'md:flex-row-reverse' : 'md:flex-row'
        )}
      >
        <motion.div
          initial={{ opacity: 0, x: textAnimationX }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{
            ease: 'easeOut',
            duration: 0.75,
          }}
          viewport={{ once: true }}
          className="flex w-full flex-col justify-center md:w-1/2 md:self-center xl:flex-1"
        >
          <RichText
            field={slice.primary.title}
            components={{
              heading2: ({ children }) => <h2 className="section-title">{children}</h2>,
              heading3: ({ children }) => <h3 className="section-title">{children}</h3>,
            }}
          />

          {isFilled.richText(slice.primary.subtitle) && (
            <RichText
              field={slice.primary.subtitle}
              components={{
                heading3: ({ children }) => <h3 className="section-subtitle mt-2 mb-3">{children}</h3>,
                heading4: ({ children }) => <h4 className="section-subtitle mt-2 mb-3">{children}</h4>,
                heading5: ({ children }) => <h5 className="section-subtitle mt-2 mb-3">{children}</h5>,
                heading6: ({ children }) => <h6 className="section-subtitle mt-2 mb-3">{children}</h6>,
              }}
            />
          )}

          {isFilled.richText(slice.primary.text) && (
            <RichText
              field={slice.primary.text}
              components={{
                paragraph: ({ children }) => (
                  <p className={cn('text-lg', !isFilled.richText(slice.primary.subtitle) && 'mt-3')}>
                    {children}
                  </p>
                ),
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                list: ({ children }) => <ul className="mt-6 list-none space-y-2">{children}</ul>,
                listItem: ({ children }) => (
                  <li className={`hover:text-primary flex items-start gap-2 text-base leading-7`}>
                    <IconComponent
                      icon="ChevronRight"
                      size="small"
                      className={`mt-2 shrink-0 ${isNonDefaultVariation ? 'text-white' : 'text-primary'}`}
                    />
                    <span>{children}</span>
                  </li>
                ),
              }}
            />
          )}

          {slice.variation === 'withAccordion' && isFilled.group(slice.primary.accordion) && (
            <div className="mt-8 space-y-1">
              {slice.primary.accordion?.map((item, index) => (
                <div key={asText(item.title)} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => toggle(index)}
                    className="flex w-full cursor-pointer items-start gap-3"
                  >
                    <div className="mt-1 shrink-0">
                      {openIndex === index ? (
                        <IconComponent
                          icon="Minus"
                          size="base"
                          className="text-primary shrink-0 transition-transform duration-300"
                        />
                      ) : (
                        <IconComponent
                          icon="Plus"
                          size="base"
                          className="text-primary shrink-0 transition-transform duration-300"
                        />
                      )}
                    </div>
                    <span>
                      <RichText
                        field={item.title}
                        components={{
                          heading5: ({ children }) => <h5 className="text-xl font-medium">{children}</h5>,
                          heading6: ({ children }) => <h6 className="text-xl font-medium">{children}</h6>,
                        }}
                      />
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {openIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          duration: 0.3,
                          ease: [0.4, 0, 0.2, 1],
                        }}
                        className="overflow-hidden"
                      >
                        <RichText
                          field={item.text}
                          components={{
                            paragraph: ({ children }) => <p className="pl-8 text-lg leading-7">{children}</p>,
                            listItem: ({ children }) => <li className="list-disc">{children}</li>,
                          }}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}

          {slice.variation === 'withDivider' && isFilled.group(slice.primary.dividegroup) && (
            <div className="divide-primary mt-6">
              {slice.primary.dividegroup.map((item, index) => (
                <div key={asText(item.title)}>
                  <RichText
                    field={item.title}
                    components={{
                      paragraph: ({ children }) => <p className="text-[22px] font-semibold">{children}</p>,
                    }}
                  />
                  <p className="text-lg">{item.text}</p>

                  {index !== slice.primary.dividegroup.length - 1 && (
                    <hr className="border-primary my-5 max-w-[70px] border-3" />
                  )}
                </div>
              ))}
            </div>
          )}

          {isFilled.group(slice.primary.buttons) && (
            <div className="group mt-3 flex items-center gap-10">
              {slice.primary.buttons.map((button) => {
                return (
                  <div key={button.button.text} className="flex items-center gap-2">
                    <svg
                      aria-label="Icon"
                      role="img"
                      xmlns="http://www.w3.org/2000/svg"
                      width="8"
                      height="12"
                      viewBox="0 0 8 12"
                      fill="none"
                      className="fill-[#C147E9] transition-colors group-hover:fill-[#810ca8]"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.351784 11.6482C0.126537 11.4229 0 11.1173 0 10.7988C0 10.4802 0.126537 10.1746 0.351784 9.94929L4.30833 5.99275L0.351784 2.0362C0.13292 1.80959 0.0118152 1.50609 0.0145528 1.19106C0.0172903 0.876032 0.143651 0.57468 0.36642 0.351912C0.589188 0.129144 0.89054 0.00278295 1.20557 4.54197e-05C1.5206 -0.00269211 1.8241 0.118413 2.05071 0.337276L6.85672 5.14328C7.08196 5.3686 7.2085 5.67415 7.2085 5.99275C7.2085 6.31134 7.08196 6.61689 6.85672 6.84221L2.05071 11.6482C1.82539 11.8735 1.51984 12 1.20125 12C0.88265 12 0.577099 11.8735 0.351784 11.6482Z"
                      />
                    </svg>
                    <ContentLink
                      field={button.button}
                      linkResolver={linkResolver}
                      className={cn(
                        'text-foreground flex items-center justify-center gap-2 text-lg font-semibold group-hover:text-[#810ca8]'
                      )}
                    >
                      {button.button.text}
                    </ContentLink>
                  </div>
                )
              })}
            </div>
          )}
        </motion.div>

        {(isFilled.image(slice.primary.image) || illustration !== undefined) && (
          <motion.div
            initial={{ opacity: 0, x: imageAnimationX }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{
              type: 'tween',
              ease: 'easeOut',
              duration: 0.75,
            }}
            viewport={{ once: true }}
            className="mb-8 w-full items-center justify-center md:mb-0 md:flex md:h-auto md:w-1/2 xl:w-[630px]"
          >
            {illustration !== undefined ? (
              <Illustration name={illustration} className="w-full" />
            ) : (
              <ContentNextImage
                field={slice.primary.image}
                fallbackAlt=""
                className="rounded-2xl object-contain"
              />
            )}
          </motion.div>
        )}
      </div>
    </section>
  )
}

export default Spotlight
